import { useEffect } from "react"

import { atom, useAtom } from "jotai"
import { atomWithHash } from "jotai/utils"
import { useRouter } from "next/router"
import { useSession } from "next-auth/react"
import { getProviders } from "next-auth/react"

import Alert from "components/Alert"
import Or from "components/dashboard/auth/Or"
import SignInCredentials from "components/dashboard/auth/SignInCredentials"
import SignInEmail from "components/dashboard/auth/SignInEmail"
import SignInOAuth from "components/dashboard/auth/SignInOAuth"
import Layout, { Container, Section } from "components/Layout"
import LoadingSkeleton from "components/LoadingSkeleton"
import SEO from "components/Seo"

import type { CustomSession } from "./api/auth/[...nextauth]"
import type { IAlert } from "components/forms/FormTemplate"
import type { GetServerSideProps, NextPage } from "next"
import type { BuiltInProviderType } from "next-auth/providers"
import type { ClientSafeProvider, LiteralUnion } from "next-auth/react"

// no need to guard against null with this type as redirecting in getServerSideProps if null
export type NextAuthProviders = Record<
  LiteralUnion<BuiltInProviderType, string>,
  ClientSafeProvider
>

export type SignInFormType = "signIn" | "signInEmail" | "signUp"

const signInFormToDisplayAtom = atomWithHash<SignInFormType>("form", "signIn", {
  replaceState: true
})
export const signInAlertAtom = atom<IAlert>({
  content: "",
  show: false,
  variant: "success"
})

// See https://next-auth.js.org/configuration/pages#sign-in-page
export const signInErrors = {
  Default:
    "Sign in failed. Try again with the way that you signed up if you already have an account.",
  OAuthCallback: "Try signing with a different account.",
  OAuthCreateAccount: "Try signing with a different account.",
  EmailCreateAccount: "Try signing with a different account.",
  Callback: "Try signing with a different account.",
  OAuthAccountNotLinked:
    "This account is not linked, please sign in the same way you did before or sign in via email.",
  EmailSignin: "There was an error registering. Please try again later.",
  CredentialsSignin:
    "Sign in failed. Check the details you provided are correct.",
  OAuthsignIn:
    "No existing user with that oauth account. Please register before signing in."
}

const SignIn: NextPage<{
  providers: NextAuthProviders
}> = ({ providers }) => {
  const [formToDisplay, setFormToDisplay] = useAtom(signInFormToDisplayAtom)
  const router = useRouter()
  const [alert, setAlert] = useAtom(signInAlertAtom)
  const { data, status: sessionStatus } = useSession()
  const session = data as CustomSession

  // hide the alert after 10 seconds whenever it is set
  useEffect(() => {
    setTimeout(() => {
      setAlert(prev => ({ ...prev, show: false }))
    }, 10000)
  }, [alert, setAlert])

  useEffect(() => {
    // set any errors from the query string
    if (router.query.error) {
      setAlert({
        content:
          signInErrors[router.query.error as "Default"] || signInErrors.Default,
        show: true,
        variant: "error"
      })
    }
    // when the session is ready, redirect to the dashboard
    if (sessionStatus === "authenticated") {
      router.push("/dashboard")
    }
  }, [session, sessionStatus, router, setAlert])

  return (
    <>
      <SEO
        titleTag={`Sign in - ${process.env.NEXT_PUBLIC_SITE_NAME}`}
        metaDescription=""
        pageTitle="Sign in"
        noIndex
      />
      <Layout>
        {/* Set the loading state on sign in if either the session is loading or there is a user on the session as they about to be redirected */}
        {sessionStatus === "loading" || session?.user ? (
          <LoadingSkeleton className="w-full h-[75vh]" />
        ) : (
          <>
            <Section variant="gray-100" className="!py-11">
              <Container>
                <div className="mdmin:w-1/2 mx-auto p-5 bg-white border border-gray-300">
                  {formToDisplay === "signIn" ? (
                    <>
                      <SignInCredentials />
                      <Or />
                      <SignInOAuth providers={providers} />
                      <div className="flex flex-wrap items-center justify-between gap-6">
                        <p
                          className="href-style with-underline pt-3 my-0"
                          onClick={() => setFormToDisplay("signInEmail")}
                        >
                          Sign in via email
                        </p>
                        <p
                          className="href-style with-underline pt-3 my-0"
                          onClick={() => setFormToDisplay("signUp")}
                        >
                          Sign up
                        </p>
                      </div>
                    </>
                  ) : formToDisplay === "signInEmail" ? (
                    <>
                      <SignInEmail variant="signInEmail" />
                      <p
                        className="href-style with-underline pt-3"
                        onClick={() => setFormToDisplay("signIn")}
                      >
                        Back to sign in
                      </p>
                    </>
                  ) : formToDisplay === "signUp" ? (
                    <>
                      <SignInEmail variant="signUp" />
                      <Or />
                      <SignInOAuth variant="signUp" providers={providers} />
                      <p
                        className="href-style with-underline pt-3"
                        onClick={() => setFormToDisplay("signIn")}
                      >
                        Back to sign in
                      </p>
                    </>
                  ) : null}
                  <Alert variant={alert.variant} show={alert.show}>
                    {alert.content}
                  </Alert>
                </div>
              </Container>
            </Section>
          </>
        )}
      </Layout>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async () => {
  const providers = await getProviders()

  // if no providers are configured redirect to homepage
  if (!providers) {
    return {
      redirect: {
        destination: "/",
        permanent: false
      }
    }
  }

  return {
    props: {
      providers
    }
  }
}

export default SignIn
